import { useCallback } from 'react';
import useAPI from './useApi';
import {
  CHECKOUT_CANCEL_RETURNS,
  SETTINGS_CHECKOUT_CANCEL_PATHS,
  getCheckoutCancelFromLocation,
} from '../components/billing/billingPlanData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const useBilling = () => {
  const { data, error, loading, request } = useAPI();

  /**
   * Start a 14-day Trade trial without Stripe checkout.
   * Track activates the free plan instead of a trial.
   * @param {'trade'|'track'} planTier
   */
  const startFreeTrial = useCallback(async (planTier) => {
    const url = `${BACKEND_URL}/billing/start_free_trial`;
    const result = await request(url, {}, 'POST', { plan_tier: planTier });
    return result;
  }, [request]);

  /** Track is free. No Stripe checkout. */
  const activateFreeTrack = useCallback(async () => {
    const url = `${BACKEND_URL}/billing/activate_free_track`;
    return request(url, {}, 'POST', {});
  }, [request]);

  /**
   * Create a Stripe Checkout session and redirect to checkout.
   * Used post-trial (upgrade) and for payment-failed recovery.
   * @param {string} plan - 'monthly' | 'annual'
   * @param {boolean} isTrial
   * @param {{ cancelReturn?: 'pricing' | 'onboarding' | 'settings' | 'flash-sale', cancelPath?: string, planTier?: 'trade' | 'track', campaignId?: string }} [options]
   */
  const createCheckoutSession = useCallback(async (plan, isTrial = false, options = {}) => {
    const url = `${BACKEND_URL}/billing/create_checkout_session`;
    const body = { plan, isTrial };
    const inferred = getCheckoutCancelFromLocation();
    const cancelReturn = CHECKOUT_CANCEL_RETURNS.includes(options.cancelReturn)
      ? options.cancelReturn
      : inferred.cancelReturn;
    body.cancelReturn = cancelReturn;
    const cancelPath = options.cancelPath || inferred.cancelPath;
    if (cancelReturn === 'settings' && SETTINGS_CHECKOUT_CANCEL_PATHS.includes(cancelPath)) {
      body.cancelPath = cancelPath;
    }
    if (options.planTier) {
      body.plan_tier = options.planTier;
    }
    if (options.campaignId) {
      body.campaign_id = options.campaignId;
    }
    const result = await request(url, {}, 'POST', body);

    if (result?.url) {
      window.location.href = result.url;
      return result;
    }

    if (result?.missingEnv) {
      throw new Error(`Checkout isn't configured (${result.missingEnv} is not set).`);
    }
    throw new Error(
      typeof result?.error === 'string' && result.error
        ? result.error
        : 'Could not start checkout. Please try again.'
    );
  }, [request]);

  /**
   * Add the one-time courtesy week after a trial ends.
   * Stripe-backed trials are extended in Stripe; the caller should refresh settings.
   */
  const extendTrial = useCallback(async () => {
    const url = `${BACKEND_URL}/billing/extend_trial`;
    const result = await request(url, {}, 'POST');
    if (result?.success) return result;
    const message = typeof result?.error === 'string' && result.error
      ? result.error
      : 'Could not add your extra week. Please try again.';
    throw new Error(message);
  }, [request]);

  /**
   * Create a Stripe Customer Portal session and redirect to portal
   */
  const createPortalSession = useCallback(async () => {
    const url = `${BACKEND_URL}/billing/create_portal_session`;
    const result = await request(url, {}, 'POST');
    
    // Redirect to Stripe Customer Portal if URL is returned
    if (result?.url) {
      window.location.href = result.url;
    }
    
    return result;
  }, [request]);

  /**
   * Get current subscription details
   */
  const getSubscription = useCallback(async () => {
    const url = `${BACKEND_URL}/billing/subscription`;
    const result = await request(url, {}, 'GET');
    return result?.subscription || null;
  }, [request]);

  return {
    data,
    error,
    loading,
    startFreeTrial,
    activateFreeTrack,
    createCheckoutSession,
    createPortalSession,
    extendTrial,
    getSubscription
  };
};

export default useBilling;

